// features/pricing/view/PricingSection.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import usePricingViewModel from "../viewModel/usePricingViewModel";
import PricingCard from "../components/PricingCard";
import SupportTicketForm from "../../../components/SupportTicketForm";
import Toast from "../../../components/Toast";
import { BillingPeriod, PricingPlan } from "../model/PricingModel";
import CurrentPlanSection from "./CurrentPlanSection";
import PriceCard from "../components/PticeCard";
import ConfirmPlanChangeDialog from "../components/ConfirmPlanChangeDialog";

interface PricingSectionProps {
  isFromSubscription?: boolean;
}

const PricingSection: React.FC<PricingSectionProps> = ({
  isFromSubscription = false,
}) => {
  const navigate = useNavigate();
  const {
    plans,
    isLoading,
    error,
    retry,
    createCheckoutSession,
    state,
    handleUpgradePlan,
    handleDowngradePlan,
    handleSwitchBillingPeriod,
    handleManageBilling,
  } = usePricingViewModel();
  const [showSupportForm, setShowSupportForm] = React.useState(false);
  const [showPlans, setShowPlans] = React.useState(false);
  const [toast, setToast] = React.useState<{
    message: string;
    type: "success" | "error" | "warning" | "info";
    isVisible: boolean;
  } | null>(null);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [loadingPlanId, setLoadingPlanId] = React.useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = React.useState<{
    plan: PricingPlan;
    billingPeriod: BillingPeriod;
    action: "billingSwitch" | "upgrade" | "downgrade";
    currentPlanName?: string;
    currentBillingPeriod?: string;
  } | null>(null);

  const showCurrentPlan = state.currentPlan?.show_current_plan === true;

  const currentPlanName = state.currentPlan?.name?.toLowerCase();
  // const handleGetStarted = async (
  //   plan: PricingPlan,
  //   billingPeriod: "monthly" | "yearly",
  //   isDowngrade?: boolean,
  // ) => {
  //   console.log("Selected Plan:", plan);
  //   if (isDowngrade) {
  //     console.log("Initiating downgrade process for plan:");
  //     await handleDowngradePlan(plan.id);
  //   }
  // if (plan.tierType === "ENTERPRISE" || plan.name === "Enterprise") {
  //   setShowSupportForm(true);
  // } else if (isFromSubscription) {
  //   let companyId = "";
  //   const storedCompany = localStorage.getItem("company");
  //   if (storedCompany) {
  //     try {
  //       const parsedCompany = JSON.parse(storedCompany);
  //       companyId = parsedCompany.id;
  //       console.log(
  //         "📦 Using stored company data as fallback:",
  //         parsedCompany.name,
  //       );
  //     } catch (e) {
  //       console.error("Failed to parse stored company data:", e);
  //     }
  //   }
  //   const checkoutSession = await createCheckoutSession({
  //     tier_id: plan.id,
  //     billing_period: billingPeriod,
  //     company_id: companyId,
  //     success_url: `${window.location.origin}/billing/success`,
  //     cancel_url: `${window.location.origin}/billing/cancel`,
  //   });
  //   window.location.href = checkoutSession.checkout_url;
  // } else {
  //   navigate(
  //     `/auth/company-signup?plan=${encodeURIComponent(
  //       plan.name.toLowerCase().replace(/\s+/g, "-"),
  //     )}&planId=${plan.id}&billing=${billingPeriod}`,
  //   );
  // }
  // };

  const handleGetStarted = async (
    plan: PricingPlan,
    billingPeriod: "monthly" | "yearly",
    isDowngrade?: boolean,
    isUpgrade?: boolean,
    isBillingSwitch?: boolean,
  ) => {
    console.log("Selected Plan:", plan);

    try {
      if (isBillingSwitch) {
        setConfirmDialog({
          plan,
          billingPeriod,
          action: "billingSwitch",
          currentPlanName: state.currentPlan?.name,
          currentBillingPeriod: state.currentPlan?.billing_period,
        });
        return;
      }

      if (isDowngrade) {
        setConfirmDialog({
          plan,
          billingPeriod,
          action: "downgrade",
          currentPlanName: state.currentPlan?.name,
          currentBillingPeriod: state.currentPlan?.billing_period,
        });
        return;
      }

      if (isUpgrade) {
        if (
          plan.tierType === "ENTERPRISE" ||
          plan.name.toLowerCase() === "enterprise"
        ) {
          setShowSupportForm(true);
          return;
        }

        setConfirmDialog({ plan, billingPeriod, action: "upgrade" });
        return;
      }

      if (plan.tierType === "ENTERPRISE" || plan.name === "Enterprise") {
        setShowSupportForm(true);
      } else if (isFromSubscription) {
        let companyId = "";
        const storedCompany = localStorage.getItem("company");
        if (storedCompany) {
          try {
            const parsedCompany = JSON.parse(storedCompany);
            companyId = parsedCompany.id;
            console.log(
              "📦 Using stored company data as fallback:",
              parsedCompany.name,
            );
          } catch (e) {
            console.error("Failed to parse stored company data:", e);
          }
        }
        const checkoutSession = await createCheckoutSession({
          tier_id: plan.id,
          billing_period: billingPeriod,
          company_id: companyId,
          success_url: `${window.location.origin}/billing/success`,
          cancel_url: `${window.location.origin}/billing/cancel`,
        });
        window.location.href = checkoutSession.checkout_url;
      } else {
        navigate(
          `/auth/company-signup?plan=${encodeURIComponent(
            plan.name.toLowerCase().replace(/\s+/g, "-"),
          )}&planId=${plan.id}&billing=${billingPeriod}`,
        );
      }
    } catch (error) {
      console.error("Error handling pricing action:", error);
      setToast({
        message:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.",
        type: "error",
        isVisible: true,
      });
    }
  };
  const handleConfirmAction = async () => {
    if (!confirmDialog) return;
    setActionLoading(true);
    try {
      if (confirmDialog.action === "upgrade") {
        await handleUpgradePlan(confirmDialog.plan.id, confirmDialog.billingPeriod);
        setToast({
          message: `Upgraded to ${confirmDialog.plan.name} successfully`,
          type: "success",
          isVisible: true,
        });
      } else if (confirmDialog.action === "downgrade") {
        await handleDowngradePlan(confirmDialog.plan.id, confirmDialog.billingPeriod);
        setToast({
          message: `Downgraded to ${confirmDialog.plan.name} successfully`,
          type: "success",
          isVisible: true,
        });
      } else {
        await handleSwitchBillingPeriod(confirmDialog.plan.id, confirmDialog.billingPeriod);
        setToast({
          message:
            confirmDialog.billingPeriod === "monthly"
              ? "Change to monthly billing scheduled successfully"
              : "Switched to yearly billing successfully",
          type: "success",
          isVisible: true,
        });
      }
      setConfirmDialog(null);
    } catch (error) {
      setConfirmDialog(null);
      setToast({
        message: error instanceof Error ? error.message : "Something went wrong. Please try again.",
        type: "error",
        isVisible: true,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse"
            >
              <div className="h-5 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-6"></div>
              <div className="space-y-2.5 mb-6">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j} className="h-4 bg-gray-200 rounded w-full"></div>
                ))}
              </div>
              <div className="h-10 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="max-w-md mx-auto bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 rounded-lg bg-red-50 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Failed to Load
          </h3>
          <p className="text-gray-600 text-sm mb-4">{error}</p>
          <button
            onClick={retry}
            className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }

    if (plans.length === 0) {
      return (
        <div className="max-w-md mx-auto bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No Plans Available
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Contact us for enterprise solutions
          </p>
          <a
            href="mailto:sales@talentkey.ai"
            className="px-5 py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors inline-block"
          >
            Contact Sales
          </a>
        </div>
      );
    }

    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              onGetStarted={handleGetStarted}
              currentPlan={state.currentPlan || undefined}
            />
          ))}
        </div>
        {/* Support Ticket Form Modal */}
        <SupportTicketForm
          isOpen={showSupportForm}
          onClose={() => setShowSupportForm(false)}
          source="LANDING_PAGE"
          initialCategory={"ENTERPRISE_PLAN"}
        />
        <div className="mt-8 text-center">
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-500 mb-2">
            <div className="flex items-center">
              <svg
                className="w-4 h-4 text-emerald-500 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              Secure payment
            </div>
            <div className="hidden sm:block">•</div>
            <div className="flex items-center">
              <svg
                className="w-4 h-4 text-emerald-500 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              14-day free trial
            </div>
            <div className="hidden sm:block">•</div>
            <div className="flex items-center">
              <svg
                className="w-4 h-4 text-emerald-500 mr-1.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              No setup fees
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Need custom pricing?{" "}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowSupportForm(true);
              }}
              className="text-blue-600 hover:text-blue-700 font-medium underline"
            >
              Contact sales
            </a>
          </p>
        </div>
      </>
    );
  };

  if (showCurrentPlan) {
    return (
      <>
        <CurrentPlanSection
          currentPlanClick={() => setShowPlans(true)}
          currentPlan={state.currentPlan}
          handleManageBilling={handleManageBilling}
        />
        <section className="py-16 lg:py-24 bg-gradient-to-b from-white to-gray-50/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* <PriceCard/> */}
            {renderContent()}
          </div>
        </section>
        {confirmDialog && (
          <ConfirmPlanChangeDialog
            plan={confirmDialog.plan}
            billingPeriod={confirmDialog.billingPeriod}
            action={confirmDialog.action}
            currentPlanName={confirmDialog.currentPlanName}
            currentBillingPeriod={confirmDialog.currentBillingPeriod}
            isLoading={actionLoading}
            onConfirm={handleConfirmAction}
            onCancel={() => setConfirmDialog(null)}
          />
        )}
        {toast?.isVisible && (
          <Toast
            message={toast.message}
            type={toast.type}
            isVisible={toast.isVisible}
            onClose={() => setToast(null)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <section
        id="pricing"
        className="py-16 lg:py-24 bg-gradient-to-b from-white to-gray-50/50"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Choose Your{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
                Perfect Plan
              </span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              <span className="font-semibold text-emerald-600">
                Free for job seekers.
              </span>{" "}
              Companies get 14-day free trials.
            </p>
          </div>
          {renderContent()}
        </div>
      </section>
      {toast?.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default PricingSection;
